const SELECTORS = ['[data-kvsche-mistress]', '[data-kvsche-temptress]'];
const ROOT_SELECTOR = SELECTORS.join(',');

class KvscheProductPage {
  constructor(root) {
    this.root = root;
    this.productType = root.hasAttribute('data-kvsche-mistress') ? 'mistress' : 'temptress';
    this.abortController = new AbortController();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.variants = this.readVariants();
    this.form = root.querySelector('.kt-product-form');
    this.buyBox = root.querySelector('[data-buy-box]');
    this.variantInput = root.querySelector('[data-variant-input]');
    this.price = root.querySelector('[data-product-price]');
    this.stock = root.querySelector('[data-product-stock]');
    this.stickyCart = root.querySelector('[data-sticky-cart]');
    this.stickyLabel = root.querySelector('[data-sticky-label]');
    this.stickyButton = root.querySelector('[data-sticky-add]');
    this.addButton = root.querySelector('[ref="addToCartButton"]');
    this.quantityInput = root.querySelector('quantity-selector-component input[ref="quantityInput"]');
  }

  init() {
    this.setupMotion();
    this.setupHeroVideos();
    this.setupReveals();
    this.setupVariants();
    this.setupStickyCart();
    this.setupGallery();
  }

  destroy() {
    this.abortController.abort();
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.stickyObserver) this.stickyObserver.disconnect();
  }

  get signal() {
    return this.abortController.signal;
  }

  readVariants() {
    const script = this.root.querySelector(`[data-${this.productType}-variants]`);
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (_error) {
      return [];
    }
  }

  setupMotion() {
    if (this.reduceMotion) {
      this.root.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
      return;
    }

    this.root.addEventListener('pointermove', this.setPointerVars, { signal: this.signal });
    this.root.addEventListener('pointerleave', this.resetPointerVars, { signal: this.signal });
  }

  setupHeroVideos() {
    const videos = this.root.querySelectorAll('.kt-hero-cinema__video');
    if (!videos.length || this.reduceMotion) return;

    videos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.removeAttribute('controls');
      video.preload = 'metadata';

      const markPlaying = () => video.classList.add('is-playing');
      video.addEventListener('playing', markPlaying, { once: true, signal: this.signal });
      video.addEventListener('canplay', markPlaying, { once: true, signal: this.signal });
      if (video.readyState >= 2) markPlaying();

      video.load();
      if (typeof video.play !== 'function') return;
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    });
  }

  setPointerVars = (event) => {
    const rect = this.root.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((event.clientY - rect.top) / Math.max(window.innerHeight, 1) - 0.5) * 2;
    this.root.style.setProperty('--kt-pointer-x', x.toFixed(3));
    this.root.style.setProperty('--kt-pointer-y', y.toFixed(3));
  };

  resetPointerVars = () => {
    this.root.style.setProperty('--kt-pointer-x', '0');
    this.root.style.setProperty('--kt-pointer-y', '0');
  };

  setupReveals() {
    const revealItems = this.root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window) || this.reduceMotion) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    );

    revealItems.forEach((item) => this.revealObserver.observe(item));
  }

  setupVariants() {
    const optionButtons = this.root.querySelectorAll('[data-purchase-option]');
    if (!optionButtons.length) {
      this.syncStickyLabel();
      return;
    }

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectVariant(button), { signal: this.signal });
    });

    this.syncStickyLabel();
  }

  selectVariant(button) {
    const variantId = Number(button.dataset.variantId);
    const variant = this.variants.find((item) => Number(item.id) === variantId);

    if (!variant || !this.variantInput) return;

    this.variantInput.value = String(variant.id);
    if (this.price) this.price.textContent = button.dataset.variantPrice || '';
    if (this.stock) this.stock.textContent = variant.available ? 'In stock' : 'Sold out';

    this.root.querySelectorAll('[data-purchase-option]').forEach((option) => {
      const isSelected = option === button;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-pressed', String(isSelected));
    });

    const optionQuantity = Number(button.dataset.optionQuantity) || 1;
    this.setQuantity(optionQuantity);

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
    }

    if (this.stickyButton) {
      this.stickyButton.disabled = !variant.available;
    }

    this.syncStickyLabel(button.dataset.variantLabel, button.dataset.variantPrice);
  }

  syncStickyLabel(variantLabel, variantPrice) {
    if (!this.stickyLabel) return;

    const selected = this.root.querySelector('[data-purchase-option].is-selected');
    const label = variantLabel || selected?.dataset.variantLabel || '';
    const price = variantPrice || selected?.dataset.variantPrice || this.price?.textContent?.trim() || '';
    const normalizedLabel = label.toLowerCase();
    let purchaseName = 'Single glass';

    if (normalizedLabel.includes('set')) {
      purchaseName = 'Set of 4';
    } else if (label) {
      purchaseName = label;
    }

    this.stickyLabel.textContent = price ? `${purchaseName} · ${price}` : purchaseName;
  }

  setupStickyCart() {
    if (!this.stickyCart) return;

    if ('IntersectionObserver' in window && this.buyBox) {
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          this.stickyCart.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { threshold: 0.08 }
      );
      this.stickyObserver.observe(this.buyBox);
    } else {
      const syncStickyCart = () => {
        this.stickyCart.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.42);
      };

      syncStickyCart();
      window.addEventListener('scroll', syncStickyCart, { passive: true, signal: this.signal });
    }

    if (this.stickyButton) {
      this.stickyButton.addEventListener(
        'click',
        () => {
          this.form?.requestSubmit();
        },
        { signal: this.signal }
      );
    }
  }

  setQuantity(quantity) {
    const quantitySelector = this.root.querySelector('quantity-selector-component');
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(quantity);
    }

    if (this.quantityInput) {
      this.quantityInput.value = String(quantity);
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  setupGallery() {
    const gallery = this.root.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const mainWrap = gallery.querySelector('.kt-product-gallery__main');
    const mainImage = gallery.querySelector('[data-gallery-main]') || mainWrap?.querySelector('img');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');

    thumbs.forEach((thumb) => {
      thumb.addEventListener(
        'click',
        () => {
          if (!mainImage || thumb.classList.contains('is-selected')) return;

          mainWrap?.classList.add('is-changing');
          window.setTimeout(
            () => {
              mainImage.removeAttribute('srcset');
              mainImage.removeAttribute('sizes');
              mainImage.src = thumb.dataset.src;
              mainImage.alt = thumb.dataset.alt || '';
              mainWrap?.classList.remove('is-changing');
            },
            this.reduceMotion ? 0 : 120
          );

          thumbs.forEach((button) => {
            const isSelected = button === thumb;
            button.classList.toggle('is-selected', isSelected);
            if (isSelected) {
              button.setAttribute('aria-current', 'true');
            } else {
              button.removeAttribute('aria-current');
            }
          });
        },
        { signal: this.signal }
      );
    });
  }
}

const instances = new WeakMap();

function initRoot(root) {
  if (instances.has(root)) return;

  const instance = new KvscheProductPage(root);
  instances.set(root, instance);
  instance.init();
}

function destroyRoot(root) {
  const instance = instances.get(root);
  if (!instance) return;

  instance.destroy();
  instances.delete(root);
}

document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(destroyRoot);
});

window.addEventListener('pageshow', () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll(`${ROOT_SELECTOR} .kt-hero-cinema__video`).forEach((video) => {
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    if (video.readyState >= 2) video.classList.add('is-playing');
    if (typeof video.play !== 'function') return;

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  });
});
